import React from 'react';
import { breakpoints, layout } from '@citrite/web-ui-component';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { bottomNavBarHeight } from 'App/BottomNav/dimensions';
import { useFormFactor } from 'App/Header/useFormFactor';
import { useClientHeight } from 'Components/AppLayout/useClientHeight';
import { useHTMLElementProperties } from 'Components/AppLayout/useHTMLElementProperties';
import { ConditionalRenderer } from 'Components/ConditionalRenderer';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary } from 'utils/useFeatureCanary';

const footerHeightLagecy = layout.largeSpace;
const footerHeightDetailed = layout.space32;
const StyledDivLegacy = styled.div<{
	isSmallFormFactor: boolean;
	clientHeight: string;
	offsetTop: number;
	offsetBottom: number;
}>`
	overflow-y: auto;
	${props =>
		props.isSmallFormFactor
			? css`
					padding: ${layout.mediumSpace};
					display: flex;
					flex-direction: column;
					width: auto;
					margin-bottom: ${bottomNavBarHeight};
					height: calc(
						${props.clientHeight} - ${props.offsetTop}px - ${layout.mediumSpace} -
							${layout.mediumSpace} - ${bottomNavBarHeight}
					);
			  `
			: css`
					height: calc(
						${props.clientHeight} - ${props.offsetTop}px - ${layout.mediumLargeSpace} -
							${layout.mediumLargeSpace} - ${footerHeightLagecy} - ${props.offsetBottom}px
					);
					padding: ${layout.mediumLargeSpace} ${layout.space64};
					position: relative;
					scrollbar-width: thin;
			  `};
`;

const StyledDivDetailed = styled(StyledDivLegacy)`
	padding: ${layout.mediumLargeSpace} ${layout.space20};
	${props =>
		css`
			height: calc(
				${props.clientHeight} - ${props.offsetTop}px - ${layout.mediumLargeSpace} -
					${layout.mediumLargeSpace} - ${footerHeightDetailed} - ${layout.space2} -
					${props.offsetBottom}px
			);
		`}
	&::-webkit-scrollbar {
		display: none;
	}
	scrollbar-color: rgba(0, 0, 0, 0.4) transparent;
`;
/**	CSS explanation:
 *	The height is being calculated by taking into consideration the
 *	height above and below the Container (offsetTop and offsetBottom) and also
 *  the top and bottom padding of the Container component (layout.mediumLargeSpace)
 *  and the height of the Footer component excluding the margin and padding(16px).
 */

const StyledInnerContainerLegacy = styled.div`
	max-width: 1060px;
	margin: 0 auto;
`;

const StyledInnerContainerDetailed = styled(StyledInnerContainerLegacy)`
	max-width: ${breakpoints.centerContentWidthPX}px;
`;

interface ContainerProps {
	id?: string;
	children: React.ReactElement | React.ReactElement[];
}

export const Container = ({ children, id }: ContainerProps) => {
	const { isSmallFormFactor, isLargeFormFactor } = useFormFactor();
	const clientHeight = useClientHeight();
	const { elementRef, offsetTop } = useHTMLElementProperties();
	const theme = useTheme();
	const isCenterAlignClassicViewEnabled = useFeatureCanary(
		FeatureFlag.CenterAlignClassicView
	);
	const offsetValue = {
		detailed: theme?.mastheadOffset?.daasBottomOffset ?? 32,
		legacy: theme?.mastheadOffset?.daasBottomOffset || 32,
	};

	const StyledDiv =
		isCenterAlignClassicViewEnabled && isLargeFormFactor
			? StyledDivDetailed
			: StyledDivLegacy;
	const offsetBottom =
		isCenterAlignClassicViewEnabled && isLargeFormFactor
			? offsetValue.detailed
			: offsetValue.legacy;

	const StyledInnerContainer =
		isCenterAlignClassicViewEnabled && isLargeFormFactor
			? StyledInnerContainerDetailed
			: StyledInnerContainerLegacy;

	return (
		<StyledDiv
			id="ContainerStyledDiv"
			data-testid={id}
			isSmallFormFactor={isSmallFormFactor}
			ref={elementRef}
			clientHeight={clientHeight}
			offsetTop={offsetTop}
			offsetBottom={offsetBottom}
		>
			<ConditionalRenderer flag={isLargeFormFactor} OnComponent={StyledInnerContainer}>
				{children}
			</ConditionalRenderer>
		</StyledDiv>
	);
};
